import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../../api";
import Button from "../../components/Button";
import Dialog from "../../components/Dialog";
import QrTable from "../../components/QrTable";
import { LoaderContext } from "../../context/LoaderContext";
import {
  StyledEmptyDataContainer,
  StyledMainContainer,
  StyledTableContainer,
} from "../../styles/global";
import { QR } from "../../types/interfaces";
import {
  StyledQRListContainer,
  StyledDeleteDialogBody,
  StyledQRListTableHeader,
} from "./styled";
import { GoPlus } from "react-icons/go";
import { MdDelete, MdPhoto } from "react-icons/md";
import { downloadImageQR } from "../../helper/functions";
import { AiOutlineFileJpg } from "react-icons/ai";
import { SiJpeg } from "react-icons/si";
import { ImSvg } from "react-icons/im";
import Header from "../../components/Header";

interface Props {}

const QRList: React.FC<Props> = () => {
  const { showLoader } = useContext(LoaderContext);
  const [qrs, setQrs] = useState<QR[] | null>(null);
  const [toBeDeletedQr, setToBeDeletedQr] = useState<QR | null>(null);
  const [toBeDownloadedQr, setToBeDownloadedQr] = useState<QR | null>(null);

  const navigate = useNavigate();

  // const [links, setLinks] = useState<object[] | null>(null);
  const FetchQrCodes = async () => {
    showLoader({
      show: true,
    });
    api
      .FetchQRCode()
      .then((res) => {
        setQrs(res.data.data);
      })
      .catch((err) => {
        toast.error(err.response?.data?.message || "Something went wrong");
      })
      .finally(() => {
        showLoader({
          show: false,
        });
      });
  };

  const handleQrDelete = async (id: string) => {
    showLoader({
      show: true,
    });
    api
      .DeleteQRCode(id)
      .then((res) => {
        toast.success(res.data.message);
        setToBeDeletedQr(null);
        FetchQrCodes();
      })
      .catch((err) => {
        toast.error(err.response?.data?.message || "Something went wrong");
      })
      .finally(() => {
        showLoader({
          show: false,
        });
      });
  };

  useEffect(() => {
    FetchQrCodes();
  }, []); // eslint-disable-line

  if (!qrs) {
    return null;
  }

  return (
    <>
      <Header title="WhatQR" />

      <Dialog
        title="Download QR"
        onClose={() => {
          setToBeDownloadedQr(null);
        }}
        open={!!toBeDownloadedQr}
      >
        <Button
          icon={<MdPhoto />}
          label="Download PNG"
          onClick={async () => {
            downloadImageQR(
              "image/png",
              toBeDownloadedQr!.qr?.secure_url,
              toBeDownloadedQr?.qrType + "-qr.png"
            );
          }}
        />
        <Button
          icon={<AiOutlineFileJpg />}
          label="Download JPG"
          onClick={() => {
            downloadImageQR(
              "image/jpg",
              toBeDownloadedQr!.qr?.secure_url,
              toBeDownloadedQr?.qrType + "-qr.jpg"
            );
          }}
        />
        <Button
          icon={<SiJpeg />}
          label="Download JPEG"
          onClick={() => {
            downloadImageQR(
              "image/jpeg",
              toBeDownloadedQr!.qr?.secure_url,
              toBeDownloadedQr?.qrType + "-qr.jpeg"
            );
          }}
        />

        <Button
          icon={<ImSvg />}
          label="Download SVG"
          onClick={() => {
            downloadImageQR(
              "image/svg+xml",
              toBeDownloadedQr!.qr?.secure_url,
              toBeDownloadedQr?.qrType + "-qr.svg"
            );
          }}
        />
      </Dialog>
      <StyledMainContainer>
        <Dialog
          title="Delete QR"
          onClose={() => {
            setToBeDeletedQr(null);
          }}
          open={!!toBeDeletedQr}
        >
          <StyledDeleteDialogBody>
            <p>
              Are you sure you want to delete QR with name{" "}
              <strong>{toBeDeletedQr?.qrName}</strong>? This action cannot be
              undone.
            </p>
            <div className="dialog-action">
              <Button
                icon={<MdDelete />}
                label="Delete"
                severity="danger"
                onClick={() => {
                  handleQrDelete(toBeDeletedQr!._id);
                }}
              />
              <Button
                label="Cancel"
                onClick={() => {
                  setToBeDeletedQr(null);
                }}
              />
            </div>
          </StyledDeleteDialogBody>
        </Dialog>
        <StyledQRListContainer>
          <StyledQRListTableHeader>
            <h2>All QR Codes</h2>

            <Button
              icon={<GoPlus />}
              label="Create new QR"
              style={{
                marginBottom: "16px",
                float: "right",
              }}
              onClick={() => {
                navigate("/generate/qr");
              }}
            />
          </StyledQRListTableHeader>

          {qrs.length > 0 ? (
            <StyledTableContainer>
              <QrTable
                data={qrs}
                onEdit={(qr: QR) => {
                  navigate(`/update-qr/${qr._id}`);
                }}
                onDelete={(qr: QR) => {
                  setToBeDeletedQr(qr);
                }}
                onDownload={(qr: QR) => {
                  setToBeDownloadedQr(qr);
                }}
              />
            </StyledTableContainer>
          ) : (
            <StyledEmptyDataContainer>
              <p>No QRs added</p>
            </StyledEmptyDataContainer>
          )}
        </StyledQRListContainer>
      </StyledMainContainer>
    </>
  );
};

export default QRList;
